/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import { PurchaserQuestionnaireQuestionHeader } from "./PurchaserQuestionnaireQuestionHeader";

import type { RankedSelectQuestionData } from "./purchaserQuestionnaireData";

type RankedSelectQuestionCardProps = {
  eventName?: string;
  question: RankedSelectQuestionData;
};

export const RankedSelectQuestionCard: React.FC<
  RankedSelectQuestionCardProps
> = ({ eventName, question }) => (
  <article css={styles.card}>
    <PurchaserQuestionnaireQuestionHeader
      eventName={eventName}
      metadata={
        <span>
          回答 {formatCount(question.totalAnswerCount)}件（単一選択・
          {formatCount(question.optionCount)}択）
        </span>
      }
      questionLabel={question.questionLabel}
      text={question.text}
    />

    <ol css={styles.answerList}>
      {question.answers.map((answer) => {
        const isFirst = answer.rank === 1;

        return (
          <li css={styles.answerItem} key={`${answer.rank}-${answer.answer}`}>
            <div css={styles.answerHeader}>
              <span css={styles.rank(isFirst)} aria-label={`${answer.rank}位`}>
                {answer.rank}
              </span>
              <span css={styles.answerLabel(isFirst)} title={answer.answer}>
                {answer.answer}
              </span>
              <span css={styles.answerCount}>
                {formatCount(answer.count)}件
              </span>
              <strong css={styles.answerPercentage}>
                {formatPercentage(answer.percentage)}
              </strong>
            </div>
            <div
              css={styles.answerBarTrack}
              aria-label={`${answer.answer}の回答割合`}
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={answer.percentage}
              role="progressbar"
            >
              <span css={styles.answerBar(answer.percentage, answer.rank)} />
            </div>
          </li>
        );
      })}
    </ol>
  </article>
);

const formatCount = (count: number): string => count.toLocaleString("ja-JP");

const formatPercentage = (percentage: number): string =>
  `${percentage.toFixed(1)}%`;

const styles = {
  card: css`
    min-width: 0;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
  answerList: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    margin: 18px 0 0;
    list-style: none;
  `,
  answerItem: css`
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  `,
  answerHeader: css`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  `,
  rank: (isFirst: boolean) => css`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    color: ${isFirst ? "#ffffff" : "#8b96a2"};
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    background: ${isFirst ? "#00c2ae" : "#243342"};
    border-radius: 50%;
  `,
  answerLabel: (isFirst: boolean) => css`
    flex: 1;
    min-width: 0;
    color: #ffffff;
    font-size: 13px;
    font-weight: ${isFirst ? 700 : 500};
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  answerCount: css`
    flex: none;
    color: #8b96a2;
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,
  answerPercentage: css`
    flex: none;
    width: 54px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 800;
    text-align: right;
    font-variant-numeric: tabular-nums;
  `,
  answerBarTrack: css`
    display: block;
    height: 10px;
    background: #243342;
    border-radius: 999px;
    overflow: hidden;
  `,
  answerBar: (percentage: number, rank: number) => {
    const opacity = Math.max(1 - (rank - 1) * 0.11, 0.35);

    return css`
      display: block;
      width: ${percentage}%;
      height: 100%;
      background: #00c2ae;
      border-radius: 999px;
      opacity: ${opacity};
    `;
  },
};
